import { useId } from 'react'

import type { SelfReportedRow, SelfReportedSignups, SignupsReport } from '@/api/signups'
import { formatCurrency, formatInteger, formatPercent, KpiCard, majorUnits, UnavailableKpiCard } from '@/design'

/** The longest range that still gets a column per day; a longer one shows the period's figures only. */
export const MAX_DAY_COLUMNS = 14

const NO_FIGURE = '—'
const MINUS = '−'

/** What the "No answer" row reads in place of an answer. */
export const NO_ANSWER_LABEL = 'No answer'

/** Why there is no Meta spend per "Facebook ad" sign-up, said in words. */
const SPEND_UNAVAILABLE: Record<string, string> = {
  no_spend: 'No Meta ad spend in this period',
  no_facebook_ad_signups: 'No sign-up answered “Facebook ad” this period',
  spend_not_convertible: 'Meta spend could not be converted into this workspace’s currency',
}

export function spendUnavailableText(reason: string | null): string {
  return (reason && SPEND_UNAVAILABLE[reason]) ?? 'Not available for this period'
}

/** A change in sign-ups as a signed count: "+85", "−12", "0". */
export function formatSignedCount(change: number): string {
  if (change > 0) return `+${formatInteger(change)}`
  if (change < 0) return `${MINUS}${formatInteger(Math.abs(change))}`
  return '0'
}

/** A tenant day's column header, "Mon 5": the date key read as a calendar day, never shifted by the browser's zone. */
function dayHeader(date: string): { short: string; long: string } {
  const day = new Date(`${date}T00:00:00Z`)
  const part = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' }).format(day)
  return {
    short: `${part({ weekday: 'short' })} ${part({ day: 'numeric' })}`,
    long: part({ weekday: 'long', day: 'numeric', month: 'long' }),
  }
}

function ChangeCell({ change }: { change: number }) {
  const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'flat'
  return (
    <td data-align="end" className={`cus-answers__change cus-answers__change--${direction}`} data-direction={direction}>
      {formatSignedCount(change)}
    </td>
  )
}

/** The headline figures above the table, as the weekly report gives them. */
function SelfReportedKpis({ split, signups }: { split: SelfReportedSignups; signups: number }) {
  const spend = split.spendPerFacebookAdSignup
  return (
    <ul className="cus-signup-kpis" aria-label="Member figures">
      <li>
        <KpiCard
          label="Members"
          value={formatInteger(split.membersAtEnd)}
          change={null}
          polarity="up_good"
          note={`${formatInteger(split.membersAtStart)} at the start of the period`}
        />
      </li>
      <li>
        <KpiCard label="New members" value={formatSignedCount(signups)} change={null} polarity="up_good" note="Sign-ups this period" />
      </li>
      <li>
        <KpiCard
          label="Member growth"
          value={split.memberGrowthPct === null ? NO_FIGURE : `${split.memberGrowthPct.toFixed(1)}%`}
          change={null}
          polarity="up_good"
          note={split.memberGrowthPct === null ? 'No members at the start of the period' : 'Sign-ups over members at the start'}
        />
      </li>
      <li>
        <KpiCard
          label="Against the previous period"
          value={split.vsPreviousRatio === null ? NO_FIGURE : `${split.vsPreviousRatio.toFixed(1)}×`}
          change={null}
          polarity="neutral"
          note={split.vsPreviousRatio === null ? 'No sign-ups in the previous period' : 'Sign-ups this period over the previous'}
        />
      </li>
      <li>
        {spend ? (
          <KpiCard
            label="Meta spend per “Facebook ad” sign-up"
            value={formatCurrency(majorUnits(spend.perSignupMinor, spend.currency), { currency: spend.currency })}
            change={null}
            polarity="neutral"
            note={`${formatCurrency(majorUnits(spend.spendMinor, spend.currency), { currency: spend.currency })} Meta spend this period`}
          />
        ) : (
          <UnavailableKpiCard label="Meta spend per “Facebook ad” sign-up" value={NO_FIGURE} note={spendUnavailableText(split.spendUnavailable)} />
        )}
      </li>
    </ul>
  )
}

function AnswerRow({ row, showDays, label }: { row: SelfReportedRow; showDays: boolean; label: string }) {
  return (
    <tr data-answer={row.answer ?? ''}>
      <th scope="row">{label}</th>
      {showDays
        ? row.points.map((point) => (
            <td key={point.date} data-align="end" className="cus-answers__day">
              {formatInteger(point.signups)}
            </td>
          ))
        : null}
      <td data-align="end" className="cus-answers__period">
        {formatInteger(row.signups)}
      </td>
      <td data-align="end">{row.share === null ? NO_FIGURE : formatPercent(row.share)}</td>
      <td data-align="end">{formatInteger(row.previous)}</td>
      <ChangeCell change={row.change} />
    </tr>
  )
}

/**
 * Sign-ups by the customer's own answer to "Where did you hear about us?", as 676's
 * weekly report lays them out: a row per answer in the tenant's fixed order, "No answer"
 * last, a column per day for a range of up to two weeks, this period, its share, the
 * previous period and the change, and a total. Self-reported, so kept apart from (and
 * labelled unlike) the measured channel split above it. Nothing when the API sent none.
 */
export function SelfReportedSection({ report }: { report: SignupsReport }) {
  const headingId = useId()
  const split = report.bySelfReported
  if (!split) return null
  const { totals, points } = report
  const showDays = points.length > 0 && points.length <= MAX_DAY_COLUMNS
  const caption = 'Sign-ups by what customers told us'
  return (
    <section aria-labelledby={headingId} className="cus-answers" data-testid="signups-self-reported">
      <h3 id={headingId} className="cus-subheading">
        What customers told us
      </h3>
      <p className="list-note" role="note">
        <strong>Self-reported:</strong> each customer’s answer to “Where did you hear about us?” when they signed up. It is
        what they said, not measured attribution, so it will not match “How they came”.
        {split.answeredShare !== null ? ` ${formatPercent(split.answeredShare)} of this period’s sign-ups answered.` : null}
      </p>
      <SelfReportedKpis split={split} signups={totals.signups} />
      <div className="ci-table-wrap" role="region" aria-label={caption} tabIndex={0}>
        <table className="ci-table cus-answers__table">
          <caption className="ci-visually-hidden">{caption}</caption>
          <thead>
            <tr>
              <th scope="col">Answer</th>
              {showDays
                ? points.map((point) => {
                    const header = dayHeader(point.date)
                    return (
                      <th key={point.date} scope="col" data-align="end" className="cus-answers__day">
                        <abbr title={header.long}>{header.short}</abbr>
                      </th>
                    )
                  })
                : null}
              <th scope="col" data-align="end">
                This period
              </th>
              <th scope="col" data-align="end">
                %
              </th>
              <th scope="col" data-align="end">
                Previous
              </th>
              <th scope="col" data-align="end">
                Change
              </th>
            </tr>
          </thead>
          <tbody>
            {split.rows.map((row) => (
              <AnswerRow key={row.answer} row={row} showDays={showDays} label={row.answer ?? NO_ANSWER_LABEL} />
            ))}
            <AnswerRow row={split.noAnswer} showDays={showDays} label={NO_ANSWER_LABEL} />
          </tbody>
          <tfoot>
            <tr data-answer="total">
              <th scope="row">Total</th>
              {showDays
                ? points.map((point) => (
                    <td key={point.date} data-align="end" className="cus-answers__day">
                      {formatInteger(point.signups)}
                    </td>
                  ))
                : null}
              <td data-align="end" className="cus-answers__period">
                {formatInteger(totals.signups)}
              </td>
              <td data-align="end">{totals.signups > 0 ? formatPercent(1) : NO_FIGURE}</td>
              <td data-align="end">{formatInteger(totals.previous)}</td>
              <ChangeCell change={totals.signups - totals.previous} />
            </tr>
          </tfoot>
        </table>
      </div>
      {!showDays && points.length > MAX_DAY_COLUMNS ? (
        <p className="list-note">Day-by-day columns show for a range of up to {MAX_DAY_COLUMNS} days.</p>
      ) : null}
    </section>
  )
}
